import React, { useCallback, useEffect, useState } from 'react';

import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { FocusContainer } from '@eika/focus-container';
import { IconTypes } from '@eika/icon';

import useTranslation, { format } from '../../../hooks/useTranslation';
import { deletePaymentItem } from '../../../services/paymentService';
import { useSigningContext } from '../../../signingContext/signingContext';

import './StopPaymentButton.scss';

interface Props {
    paymentId: string;
    paymentType: string | undefined;
    onExpandDialog?: (hideButtonStatus: boolean) => void;
    setStatusToStopping?: () => void;
}

const StopPaymentButton: React.FC<Props> = ({
    paymentId,
    paymentType = 'PAYMENT',
    onExpandDialog,
    setStatusToStopping,
}) => {
    const translated = useTranslation();
    const [showStopPaymentDialogue, setShowStopPaymentDialogue] = useState(false);

    const stopPayment = useCallback((id: string) => deletePaymentItem({ value: id }), []);

    const { startSigning, signingOperationPending } = useSigningContext(stopPayment);

    const handleStopPayment = useCallback(() => {
        setStatusToStopping?.();
        startSigning(paymentId);
    }, [startSigning, paymentId, setStatusToStopping]);

    const getHeaderText = useCallback(() => {
        switch (paymentType) {
            case 'TRANSFER':
            case 'RECURRING_TRANSFER':
                return format(translated.stopFuturePayment.header, translated.stopFuturePayment.headerParamTransfer);
            case 'EFAKTURA':
                return format(translated.stopFuturePayment.header, translated.stopFuturePayment.headerParamEFaktura);
            default:
                return format(translated.stopFuturePayment.header, translated.stopFuturePayment.headerParamPayment);
        }
    }, [paymentType, translated]);

    useEffect(() => {
        onExpandDialog?.(showStopPaymentDialogue);
    }, [showStopPaymentDialogue, onExpandDialog]);

    return (
        <div className="stop-payment-button__container">
            <FocusContainer entry={showStopPaymentDialogue} onExit={() => setShowStopPaymentDialogue(false)}>
                <section
                    className="stop-payment-button__section"
                    aria-labelledby="stop-payment-header"
                    hidden={!showStopPaymentDialogue}
                >
                    <h2 id="stop-payment-header" className="stop-payment-button__header">
                        {getHeaderText()}
                    </h2>
                    <div className="stop-payment-button__button-row">
                        <LinkButton
                            onClick={() => setShowStopPaymentDialogue(false)}
                            disabled={signingOperationPending}
                        >
                            {translated.stopFuturePayment.cancel}
                        </LinkButton>
                        <ButtonWithSpinner
                            buttonType="positive"
                            onClick={handleStopPayment}
                            showSpinner={signingOperationPending}
                        >
                            {translated.stopFuturePayment.confirm}
                        </ButtonWithSpinner>
                    </div>
                </section>
            </FocusContainer>
            {!showStopPaymentDialogue && (
                <LinkButton
                    className="future-payment-details__stop-btn"
                    onClick={() => setShowStopPaymentDialogue(true)}
                    icon={IconTypes.SLETT_24}
                    hidden={showStopPaymentDialogue}
                    data-testid="delete-future-payment-button"
                >
                    {translated.delete}
                </LinkButton>
            )}
        </div>
    );
};

export default StopPaymentButton;
